import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { Utente, Sede } from '../../types';
import { getRuoloLabel } from '../../utils/ruoliLabel';
import '../../styles/impostazioni.css';

type FormData = {
  email: string;
  nome: string;
  ruolo: string;
  societaIds: number[];
  sedeIds: number[];
  attivo: boolean;
  gestisceAssistenza: boolean;
};

const EMPTY: FormData = { email: '', nome: '', ruolo: 'UTENTE', societaIds: [], sedeIds: [], attivo: true, gestisceAssistenza: false };
const RUOLI = ['SUPERADMIN', 'UTENTE'];

export default function UtentiPage() {
  const { utente: utenteCorrente } = usePermesso();
  const [items,      setItems]      = useState<Utente[]>([]);
  const [sediTutte,  setSediTutte]  = useState<Sede[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [modal,      setModal]      = useState(false);
  const [form,       setForm]       = useState<FormData>({ ...EMPTY });
  const [editId,     setEditId]     = useState<number | null>(null);
  const [saving,     setSaving]     = useState(false);
  const [fRuolo,     setFRuolo]     = useState('');

  const {
    societaId: fSocietaIdStr,
    setSocietaId: setFSocietaIdStr,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const fSocietaId = fSocietaIdStr === '' ? '' : Number(fSocietaIdStr);
  const setFSocietaId = (v: number | '') => setFSocietaIdStr(v === '' ? '' : String(v));

  const itemsFiltrati = useMemo(() => {
    return items.filter(u => {
      // Chi è collegato resta sempre in lista: un SUPERADMIN spesso non è
      // associato a nessuna società e altrimenti sparirebbe dalla propria
      // pagina utenti, senza poter modificare il proprio account.
      const seStesso = utenteCorrente?.id === u.id;
      if (!seStesso && fSocietaId !== '' && !(u.societa ?? []).some(s => s.id === fSocietaId)) return false;
      if (fRuolo !== '' && u.ruolo !== fRuolo) return false;
      return true;
    });
  }, [items, fSocietaId, fRuolo, utenteCorrente]);

  const load = async () => {
    if (!pronta) return;
    try {
      const [utRes, seRes] = await Promise.all([
        apiFetch<{ utenti: Utente[] }>('/api/utenti'),
        apiFetch<{ sedi: Sede[] }>('/api/sedi'),
      ]);
      setItems(utRes.utenti);
      setSediTutte(seRes.sedi ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [pronta]); // eslint-disable-line

  // Sedi disponibili = sedi delle società selezionate nel form
  const sediDisponibili = useMemo(() => {
    if (form.societaIds.length === 0) return [];
    return sediTutte.filter(s => form.societaIds.includes(s.societaId));
  }, [sediTutte, form.societaIds]);

  // Sedi per società (raggruppate) — usate nel modal
  const sediPerSocieta = useMemo(() => {
    const map: Record<number, { societaNome: string; sedi: Sede[] }> = {};
    for (const sede of sediDisponibili) {
      if (!map[sede.societaId]) {
        const soc = societa.find(s => s.id === sede.societaId);
        map[sede.societaId] = { societaNome: soc?.nome ?? String(sede.societaId), sedi: [] };
      }
      map[sede.societaId].sedi.push(sede);
    }
    return Object.entries(map).map(([id, v]) => ({ societaId: Number(id), ...v }));
  }, [sediDisponibili, societa]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setEditId(null); setModal(true);
  };

  const openEdit = (u: Utente) => {
    setForm({
      email:      u.email,
      nome:       u.nome,
      ruolo:      u.ruolo,
      societaIds: (u.societa ?? []).map((s) => s.id),
      sedeIds:    (u.sedi ?? []).map((s) => s.id),
      attivo:     u.attivo,
      gestisceAssistenza: !!u.gestisceAssistenza,
    });
    setEditId(u.id); setModal(true);
  };

  const closeModal = () => { setModal(false); setError(''); };

  const toggleSocieta = (id: number) => {
    setForm((prev) => {
      const newSocietaIds = prev.societaIds.includes(id)
        ? prev.societaIds.filter((s) => s !== id)
        : [...prev.societaIds, id];
      // Deseleziona le sedi delle società rimosse
      const newSedeIds = prev.sedeIds.filter(sedeId => {
        const sede = sediTutte.find(s => s.id === sedeId);
        return sede && newSocietaIds.includes(sede.societaId);
      });
      return { ...prev, societaIds: newSocietaIds, sedeIds: newSedeIds };
    });
  };

  const toggleSede = (id: number) => {
    setForm((prev) => ({
      ...prev,
      sedeIds: prev.sedeIds.includes(id)
        ? prev.sedeIds.filter((s) => s !== id)
        : [...prev.sedeIds, id],
    }));
  };

  const selectAllSediSocieta = (societaId: number) => {
    const ids = sediTutte.filter(s => s.societaId === societaId).map(s => s.id);
    setForm(prev => ({
      ...prev,
      sedeIds: Array.from(new Set([...prev.sedeIds, ...ids])),
    }));
  };

  const clearAllSediSocieta = (societaId: number) => {
    const ids = new Set(sediTutte.filter(s => s.societaId === societaId).map(s => s.id));
    setForm(prev => ({ ...prev, sedeIds: prev.sedeIds.filter(id => !ids.has(id)) }));
  };

  const save = async () => {
    if (!form.nome || (!editId && !form.email)) {
      setError('Nome ed email sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (editId) await apiFetch(`/api/utenti/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
      else        await apiFetch('/api/utenti',            { method: 'POST', body: JSON.stringify(body) });
      closeModal(); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const deactivate = async (id: number, attivo: boolean) => {
    if (!window.confirm(attivo ? 'Disattivare questo utente?' : 'Riattivare questo utente?')) return;
    try {
      const u = items.find(u => u.id === id);
      await apiFetch(`/api/utenti/${id}`, { method: 'PUT', body: JSON.stringify({ attivo: !attivo, nome: u?.nome, ruolo: u?.ruolo }) });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const mostraSedi = form.ruolo === 'UTENTE';

  const columns = useMemo<BopExcelColumn<Utente>[]>(() => [
    {
      key: 'nome',
      header: 'Nome',
      accessor: (u) => u.nome,
      filter: { type: 'text' },
      cellClassName: 'utenti-td-nome',
      render: (u) => <strong>{u.nome}</strong>,
    },
    {
      key: 'email',
      header: 'Email',
      accessor: (u) => u.email,
      filter: { type: 'text' },
      cellClassName: 'bop-text-muted',
      render: (u) => u.email,
    },
    {
      key: 'ruolo',
      header: 'Ruolo',
      accessor: (u) => u.ruolo,
      filter: {
        type: 'enum',
        options: RUOLI.map(r => ({ value: r, label: getRuoloLabel(r) })),
      },
      width: '130px',
      render: (u) => <span className="bop-badge bop-badge-neutral">{getRuoloLabel(u.ruolo)}</span>,
    },
    {
      key: 'societa',
      header: 'Società',
      accessor: (u) => (u.societa ?? []).map(s => s.nome).join(' '),
      filter: { type: 'text' },
      cellClassName: 'impostazioni-utenti-td-societa',
      render: (u) => (
        <span
          className="utenti-societa-text"
          title={(u.societa ?? []).map(s => s.nome).join(', ') || undefined}
        >
          {(u.societa ?? []).length > 0
            ? (u.societa ?? []).map(s => s.nome).join(', ')
            : '—'}
        </span>
      ),
    },
    {
      key: 'gestisceAssistenza',
      header: 'Assistenza',
      accessor: (u) => u.gestisceAssistenza ? 'SI' : 'NO',
      filter: {
        type: 'enum',
        options: [
          { value: 'SI', label: 'Gestisce assistenza' },
          { value: 'NO', label: 'Non gestisce' },
        ],
      },
      width: '120px',
      render: (u) => (
        u.gestisceAssistenza
          ? <span className="bop-badge bop-badge--info" title="Vede e gestisce i ticket di tutti">Assistenza</span>
          : <span className="bop-text-muted">—</span>
      ),
    },
    {
      key: 'attivo',
      header: 'Stato',
      accessor: (u) => u.attivo ? 'ATTIVO' : 'DISATTIVO',
      filter: {
        type: 'enum',
        options: [
          { value: 'ATTIVO',    label: 'Attivo' },
          { value: 'DISATTIVO', label: 'Disattivo' },
        ],
      },
      width: '110px',
      render: (u) => (
        <span className={`bop-badge ${u.attivo ? 'bop-badge-success' : 'bop-badge-danger'}`}>
          {u.attivo ? 'Attivo' : 'Disattivo'}
        </span>
      ),
    },
  ], []);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="utenti-header-text">
          <h1 className="bop-page-title">Utenti</h1>
          <p className="bop-text-muted">Gestione degli account di accesso al backoffice</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuovo utente</button>
      </div>

      {error && !modal && <div className="inline-error">{error}</div>}

      <div className="bop-filter-bar impostazioni-utenti-filters">
        {!isUnica && (
          <select
            className="bop-select impostazioni-utenti-select-societa"
            value={fSocietaId}
            onChange={e => setFSocietaId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select
          className="bop-select impostazioni-utenti-select-ruolo"
          value={fRuolo}
          onChange={e => setFRuolo(e.target.value)}
        >
          <option value="">Tutti i ruoli</option>
          {RUOLI.map(r => <option key={r} value={r}>{getRuoloLabel(r)}</option>)}
        </select>
      </div>

      <BopExcelTable<Utente>
        data={itemsFiltrati}
        loading={loading}
        rowKey={(u) => u.id}
        onRowClick={(u) => openEdit(u)}
        initialSort={{ key: 'nome', dir: 'asc' }}
        emptyState={
          <div className="bop-empty-state">{items.length === 0 ? 'Nessun utente.' : 'Nessun utente corrisponde ai filtri selezionati.'}</div>
        }
        columns={columns}
        rowActions={(u) => (
          <BopRowActions actions={[
            { icon: <RowIcon.Disable />, label: u.attivo ? 'Disattiva' : 'Riattiva',
              onClick: () => deactivate(u.id, u.attivo),
              variant: u.attivo ? 'danger' : 'default' },
          ]} />
        )}
      />

      {modal && (
        <Modal
          title={editId ? 'Modifica utente' : 'Nuovo utente'}
          onClose={closeModal}
          footer={
            <><button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button></>
          }
        >
          {error && <div className="inline-error">{error}</div>}

          {!editId && (
            <div className="bop-form-group">
              <label className="bop-label">Email <span className="req">*</span></label>
              <input className="bop-field__input" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Nome <span className="req">*</span></label>
            <input className="bop-field__input" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Ruolo</label>
            <select className="bop-field__select" value={form.ruolo}
              onChange={(e) => setForm({ ...form, ruolo: e.target.value })}>
              {RUOLI.map((r) => <option key={r} value={r}>{getRuoloLabel(r)}</option>)}
            </select>
          </div>

          {/* Multi-selezione società */}
          <div className="bop-form-group">
            <label className="bop-label">Società associate</label>
            <div className="impostazioni-utenti-societa-box">
              {societa.length === 0 && (
                <span className="impostazioni-text-empty">Nessuna società disponibile</span>
              )}
              {societa.map((s) => (
                <label key={s.id} className="form-check impostazioni-utenti-checkbox-item">
                  <input
                    type="checkbox"
                    checked={form.societaIds.includes(s.id)}
                    onChange={() => toggleSocieta(s.id)}
                  />
                  {s.nome}
                </label>
              ))}
            </div>
          </div>

          {/* Sedi associate — solo per utenti operativi */}
          {!mostraSedi ? (
            <div className="bop-form-group">
              <label className="bop-label">Sedi associate</label>
              <p className="impostazioni-utenti-info-box">
                Gli utenti Admin e Superadmin accedono automaticamente a tutte le sedi.
              </p>
            </div>
          ) : (
            <div className="bop-form-group">
              <label className="bop-label">Sedi associate</label>
              {form.societaIds.length === 0 ? (
                <p className="impostazioni-utenti-info-box impostazioni-text-empty">
                  Seleziona prima una società per vedere le sedi disponibili.
                </p>
              ) : sediDisponibili.length === 0 ? (
                <p className="impostazioni-utenti-info-box impostazioni-text-empty">
                  Nessuna sede trovata per le società selezionate.
                </p>
              ) : (
                <div className="impostazioni-utenti-sedi-box">
                  {sediPerSocieta.map(({ societaId, societaNome, sedi }) => (
                    <div key={societaId} className={sediPerSocieta.length > 1 ? 'impostazioni-utenti-sedi-group' : ''}>
                      {sediPerSocieta.length > 1 && (
                        <div className="impostazioni-utenti-sedi-header">
                          <span className="impostazioni-utenti-sedi-label">
                            {societaNome}
                          </span>
                          <div className="impostazioni-utenti-sedi-actions">
                            <button type="button" className="impostazioni-utenti-sedi-btn"
                              onClick={() => selectAllSediSocieta(societaId)}>Tutte</button>
                            <button type="button" className="impostazioni-utenti-sedi-btn impostazioni-text-muted"
                              onClick={() => clearAllSediSocieta(societaId)}>Nessuna</button>
                          </div>
                        </div>
                      )}
                      {sedi.map(s => (
                        <label key={s.id} className={`form-check impostazioni-utenti-checkbox-item ${sediPerSocieta.length > 1 ? 'impostazioni-utenti-checkbox-indented' : ''}`}>
                          <input
                            type="checkbox"
                            checked={form.sedeIds.includes(s.id)}
                            onChange={() => toggleSede(s.id)}
                          />
                          {s.nome}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bop-form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.gestisceAssistenza}
                onChange={(e) => setForm({ ...form, gestisceAssistenza: e.target.checked })} />
              Gestisce assistenza
            </label>
            <p className="bop-text-muted">
              Vede e gestisce le richieste di tutti, risponde come staff e riceve le notifiche dei ticket.
            </p>
          </div>

          {editId && (
            <div className="bop-form-group">
              <label className="form-check">
                <input type="checkbox" checked={form.attivo}
                  onChange={(e) => setForm({ ...form, attivo: e.target.checked })} />
                Utente attivo
              </label>
            </div>
          )}
        </Modal>
      )}
    </Layout>
  );
}
